<template>
  <div class="gene-browser-container">
    <div class="module-layout">
      <!-- Left Control Panel -->
      <div class="parameter-panel">
        <h4><i class="el-icon-s-data"></i> Protein Selection</h4>
        <div class="parameter-container">
          <div class="parameter-group">
            <label>
              Select Proteins
              <el-tooltip content="Copy Selected Proteins" placement="top">
                <el-button type="text" icon="el-icon-document-copy" @click="copyGenes" size="mini" 
                  :disabled="selectedGenes.length === 0" />
              </el-tooltip>
            </label>
            <el-select
              v-model="selectedGenes"
              multiple
              filterable
              clearable
              remote
              :reserve-keyword="false"
              :remote-method="fetchGenes"
              :loading="loadingGenes"
              placeholder="Type to search proteins"
              style="width: 100%"
              @paste.native="handleGenePaste($event)"
            >
              <el-option v-for="gene in gene_list" :key="gene" :label="gene" :value="gene" />
            </el-select>
          </div>
          
          <div class="parameter-group">
            <span class="selected-info">{{ selectedGenes.length }} proteins selected</span>
          </div>

        </div>
      </div>

      <!-- Right Visualization Area -->
      <div class="results-panel">
        <div class="results-header">
          <div class="results-title">
            <i class="el-icon-s-data"></i>
            <span>Expression Analysis</span>
          </div>
          <div class="sub-tabs">
            <div class="sub-tabs-left">
              <button class="sub-tab" :class="{ active: activeTab === 'overview' }" @click="activeTab = 'overview'">
                <i class="el-icon-s-marketing"></i> Overview
              </button>
              <button class="sub-tab" :class="{ active: activeTab === 'distribution' }" @click="activeTab = 'distribution'">
                <i class="el-icon-s-data"></i> Distribution
              </button>
            </div>
          </div>
        </div>
        <div class="results-container">
          <div v-show="activeTab === 'overview'" class="plot-content" v-loading="loadingLollipop">
            <div 
              v-if="lollipopPlot.data && lollipopPlot.data.length > 0"
              class="lollipop-chart-wrapper"
            >
              <Plot
                :data="lollipopPlot.data"
                :layout="getLollipopLayout()"
                :config="getLollipopConfig()"
                @click="handleLollipopClick"
              />
            </div>
            <div v-else class="plot-placeholder">
              <i class="el-icon-s-marketing"></i>
              <p>Protein Expression Overview</p>
              <span>Select proteins to view expression across datasets. Click on a dataset to view details.</span>
            </div>
          </div>

          <div v-show="activeTab === 'distribution'" v-loading="loadingBox" class="distribution-container">
            <template v-if="boxPlots && boxPlots.length > 0">
              <div v-for="(plot, index) in boxPlots" :key="index" class="boxplot-card">
                <div class="boxplot-header" @click="navigateToProject(plot.dataset_id)">
                  <div class="boxplot-title">{{ plot.article_title || plot.dataset }}</div>
                  <div v-if="plot.pubmed_id" class="boxplot-pmid">
                    PMID: {{ plot.pubmed_id }}
                  </div>
                </div>
                <div class="boxplot-content">
                  <Plot
                    :data="plot.data"
                    :layout="getResponsivePlotLayout(plot)"
                    :config="getResponsivePlotConfig(plot)"
                  />
                </div>
              </div>
            </template>
            <div v-else class="plot-placeholder">
              <i class="el-icon-s-data"></i>
              <p>Gene Expression Distribution</p>
              <span>Select proteins to view distribution across all datasets.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'GeneBrowser',
  data() {
    return {
      selectedGenes: [],
      gene_list: [],
      allGenes: [],
      lollipopPlot: { data: [], layout: {}, config: {} },
      boxPlots: [],
      loadingGenes: false,
      loadingLollipop: false,
      loadingBox: false,
      activeTab: 'overview'
    }
  },
  created() {
    // Debounce updates so we don't spam the backend while selecting/typing.
    this._fetchLollipopPlotDebounced = this.debounce(this.fetchLollipopPlot, 400)
    this._fetchBoxPlotsDebounced = this.debounce(this.fetchBoxPlots, 600)
    this._lollipopReqId = 0
    this._boxReqId = 0
  },
  watch: {
    selectedGenes: {
      handler(newVal) {
        const genes = Array.isArray(newVal) ? newVal : []
        if (genes.length === 0) {
          this.lollipopPlot = { data: [], layout: {}, config: {} }
          this.boxPlots = []
          return
        }
        if (this._fetchLollipopPlotDebounced) this._fetchLollipopPlotDebounced()
        if (this._fetchBoxPlotsDebounced) this._fetchBoxPlotsDebounced()
      },
      deep: true
    }
  },
  async mounted() {
    await this.loadAllGenes();
  },
  methods: {
    debounce(fn, wait = 300) {
      let t = null
      return function (...args) {
        clearTimeout(t)
        t = setTimeout(() => fn.apply(this, args), wait)
      }
    },

    getLollipopLayout() {
      return { ...(this.lollipopPlot.layout || {}), autosize: true }
    },

    getLollipopConfig() {
      return { ...(this.lollipopPlot.config || {}), responsive: true }
    },

    handleLollipopClick(data) {
      if (data.points && data.points.length > 0) {
        const point = data.points[0]
        // Backend sends [count, dataset_id, ...] in customdata
        const datasetId = point.customdata && point.customdata[1]
        if (datasetId) {
          this.$router.push({ name: 'project-detail', params: { id: datasetId } })
        }
      }
    },

    getResponsivePlotConfig(plot) {
      const base = (plot && plot.config) ? plot.config : {}
      return { ...base, responsive: true }
    },

    getResponsivePlotLayout(plot) {
      const base = (plot && plot.layout) ? plot.layout : {}
      // autosize helps Plotly reflow to container size.
      return { ...base, autosize: true }
    },

    async loadAllGenes() {
      try {
        const response = await axios.post('/api/geneSearch/search_genes', { search: '' })
        if (response.data.code === 200) {
          const list = response.data.data?.content?.search_list || []
          this.allGenes = list
          this.gene_list = list.slice(0, 100)
        }
      } catch (error) {
        console.error('Error loading all genes:', error)
        this.$message.error('Failed to load gene list')
      }
    },
    
    async fetchGenes(query) {
      try {
        this.loadingGenes = true
        
        if (!query || String(query).trim() === '') {
          this.gene_list = this.allGenes.slice(0, 100)
          return
        }
        
        const response = await axios.post('/api/geneSearch/search_genes', { search: query })
        const list = response.data?.data?.content?.search_list || []
        this.gene_list = Array.isArray(list) ? list : []
        
      } catch (error) {
        console.error('Error fetching gene options:', error)
        this.gene_list = []
      } finally {
        this.loadingGenes = false
      }
    },

    async fetchLollipopPlot() {
      const genes = Array.isArray(this.selectedGenes) ? this.selectedGenes : []
      if (genes.length === 0) return

      const reqId = (this._lollipopReqId || 0) + 1
      this._lollipopReqId = reqId
      this.loadingLollipop = true

      try {
        const lollipopResponse = await axios.post('/api/geneSearch/get_gene_expression_by_dataset', {
          genes
        })

        // Ignore stale responses.
        if (reqId !== this._lollipopReqId) return

        if (lollipopResponse.data.code === 200) {
          this.lollipopPlot = lollipopResponse.data.data.lollipopPlot || { data: [], layout: {}, config: {} }

          if (lollipopResponse.data.data.missing_genes?.length > 0) {
            this.$message.warning(`Some genes not found: ${lollipopResponse.data.data.missing_genes.join(', ')}`)
          }
        }
      } catch (error) {
        if (reqId !== this._lollipopReqId) return
        console.error('Error fetching lollipop plot:', error)
        this.$message.error('Failed to fetch overview plot')
      } finally {
        if (reqId === this._lollipopReqId) this.loadingLollipop = false
      }
    },

    async fetchBoxPlots() {
      const genes = Array.isArray(this.selectedGenes) ? this.selectedGenes : []
      if (genes.length === 0) return
      const reqId = (this._boxReqId || 0) + 1
      this._boxReqId = reqId
      this.loadingBox = true
      try {
        const boxResponse = await axios.post('/api/geneSearch/get_gene_expression_profile', {
          genes,
          groupby: 'meta_sampleType'
        })

        // Ignore stale responses.
        if (reqId !== this._boxReqId) return

        if (boxResponse.data.code === 200) {
          this.boxPlots = boxResponse.data.data.boxPlots || []
        }
      } catch (error) {
        if (reqId !== this._boxReqId) return
        console.error('Error fetching box plots:', error)
        this.$message.error('Failed to fetch distribution plots')
      } finally {
        if (reqId === this._boxReqId) this.loadingBox = false
      }
    },
    
    navigateToProject(datasetId) {
      if (datasetId) {
        this.$router.push({ name: 'project-detail', params: { id: datasetId } })
      }
    },
    
    async copyGenes() {
      try {
        const text = (Array.isArray(this.selectedGenes) ? this.selectedGenes : []).join(', ')
        if (!text) return
        await navigator.clipboard.writeText(text)
        this.$notify({ title: 'Success', message: 'Copied genes to clipboard', type: 'success', position: 'top-right' })
      } catch (err) {
        try {
          const ta = document.createElement('textarea')
          ta.value = (Array.isArray(this.selectedGenes) ? this.selectedGenes : []).join(', ')
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
          this.$notify({ title: 'Success', message: 'Copied genes to clipboard', type: 'success', position: 'top-right' })
        } catch (e) {
          this.$notify({ title: 'Error', message: 'Failed to copy genes', type: 'error', position: 'top-right' })
        }
      }
    },
    
    handleGenePaste(e) {
      try {
        const text = (e && e.clipboardData && e.clipboardData.getData) ? e.clipboardData.getData('text') : ''
        if (!text) return
        e.preventDefault()
        const parsed = text.split(/[\n,;\t ]+/).map(s => (s || '').trim()).filter(Boolean)
        if (!parsed.length) return
        const merged = Array.from(new Set([...(Array.isArray(this.selectedGenes) ? this.selectedGenes : []), ...parsed]))
        this.selectedGenes = merged
      } catch (err) {
        // no-op
      }
    }
  }
}
</script>

<style scoped>
.gene-browser-container {
  background: #f8f9fa;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.module-layout {
  display: flex;
  flex-direction: row;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
}

/* Parameter Panel */
.parameter-panel {
  background: white;
  border-radius: 8px;
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.parameter-panel h4 {
  margin: 0;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.parameter-panel h4 i {
  color: #3b82f6;
}

.parameter-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 16px;
  gap: 12px;
}

.parameter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.parameter-group label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.selected-info {
  font-size: 13px;
  color: #64748b;
}

.parameter-footer {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.parameter-footer .el-button {
  width: 100%;
  font-size: 14px;
  font-weight: 600;
}

/* Results Panel */
.results-panel {
  flex: 1;
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.results-header {
  margin: 0;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.results-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.results-title i {
  color: #3b82f6;
}

/* Sub-tabs - Folder Style */
.sub-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 6px 10px;
  border-radius: 8px;
  margin-left: auto;
}

.sub-tabs-left {
  display: flex;
  gap: 2px;
}

.sub-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sub-tab:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #3b82f6;
}

.sub-tab.active {
  background: #ffffff;
  color: #3b82f6;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sub-tab.active i {
  color: #3b82f6;
}

.sub-tab i {
  font-size: 13px;
}

.results-container {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.plot-content {
  flex: 1;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.plot-content > div:not(.plot-placeholder) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.lollipop-chart-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  overflow: auto;
}

.distribution-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 90%;
  max-width: 1000px;
  margin: 0 auto;
}

.boxplot-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.boxplot-header {
  padding: 16px 20px;
  background: linear-gradient(to bottom, #fafbfc, #f9fafb);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.boxplot-header:hover {
  background: linear-gradient(to bottom, #f0f4ff, #e8f0fe);
  border-bottom-color: #3b82f6;
}

.boxplot-header:hover .boxplot-title {
  color: #2563eb;
}

.boxplot-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
  transition: color 0.2s ease;
}

.boxplot-pmid {
  font-size: 14px;
  color: #c2410c;
  font-weight: 500;
  white-space: nowrap;
  padding: 4px 10px;
  background: #fff7ed;
  border-radius: 6px;
  border: 1px solid #fed7aa;
  transition: all 0.2s ease;
}

.boxplot-header:hover .boxplot-pmid {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.boxplot-content {
  padding: 8px;
  background: white;
}

.plot-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  gap: 12px;
  padding: 48px;
}

.plot-placeholder i {
  font-size: 48px;
  color: #cbd5e1;
}

.plot-placeholder p {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
}

.plot-placeholder span {
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  max-width: 300px;
}

/* Responsive */
@media (max-width: 1024px) {
  .module-layout {
    flex-direction: column;
  }

  .parameter-panel {
    width: 100%;
  }

  .results-panel {
    min-height: 500px;
  }
}
</style>
